import { Component, computed, inject, signal, viewChild } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { environment } from '../environments/environment';
import { AuthService } from './core/auth-service';
import { I18nService } from './core/i18n-service';
import { gateOpen } from './core/dev-gate';
import { LinkService, PREVIEW_SUFFIX } from './core/link-service';
import { LANGUAGE_NAME } from './core/messages';
import { ThemeMode, ThemeService } from './core/theme-service';
import { isStopped } from './model/cortito';
import { ApiDialog } from './shared/api-dialog/api-dialog';
import { DevGate } from './shared/dev-gate/dev-gate';

const THEME_ICON: Record<ThemeMode, string> = {
  light: 'light_mode',
  dark: 'dark_mode',
  system: 'brightness_auto',
};

declare global {
  interface Window {
    /** Set by the early redirect script in `index.html`. */
    __cortito?: Promise<string | null | { protected: true }>;
  }
}

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, RouterLink, RouterLinkActive, ApiDialog, DevGate],
  templateUrl: './app.html',
})
export class App {
  private readonly links = inject(LinkService);
  private readonly router = inject(Router);

  /** Only there while somebody is signed in, which is who has tokens. */
  protected readonly apiDialog = viewChild(ApiDialog);

  protected readonly auth = inject(AuthService);
  protected readonly theme = inject(ThemeService);
  protected readonly i18n = inject(I18nService);
  protected readonly t = this.i18n.t;
  protected readonly themeButton = computed(() => ({
    icon: THEME_ICON[this.theme.mode()],
    label: this.t().theme[this.theme.mode()],
  }));
  protected readonly languageButton = computed(() => ({
    code: this.i18n.lang().toUpperCase(),
    name: LANGUAGE_NAME[this.i18n.lang()],
  }));
  protected readonly appVersion = environment.appVersion;
  /** Marks the header, the footer and the tab, so dev is never mistaken for the real one. */
  protected readonly isDev = environment.dev;

  /** The UI stays hidden while a `corti.to/<alias>` request is being resolved. */
  protected readonly resolving = signal(true);

  /**
   * Whether the app may draw itself. Always true in production; on dev it waits
   * for the key. Resolving an alias happens either way — a dev short link is made
   * to be followed.
   */
  protected readonly open = signal(gateOpen());

  constructor() {
    void this.resolveAlias();
  }

  private async resolveAlias(): Promise<void> {
    const alias = decodeURIComponent(window.location.pathname).replace(/^\/+/, '').trim();
    if (!alias) {
      this.resolving.set(false);
      return;
    }

    // A `+` asks to see where the link goes: no lookup here, no visit, and the
    // preview page does the asking.
    if (alias.endsWith(PREVIEW_SUFFIX)) {
      this.resolving.set(false);
      await this.router.navigate(['/preview', alias.slice(0, -PREVIEW_SUFFIX.length)], {
        replaceUrl: true,
      });
      return;
    }

    try {
      // The script in `index.html` starts resolving the alias long before the
      // app boots, so most of the time the answer is already here.
      const early = await window.__cortito?.catch(() => undefined);
      if (typeof early === 'string') {
        window.location.replace(early); // Already counted; this only re-arms a stalled jump.
        return;
      }

      if (early && typeof early === 'object' && early.protected) {
        this.open.set(true);
        this.resolving.set(false);
        await this.router.navigate(['/preview', alias], { replaceUrl: true });
        return;
      }

      const link = early === null ? null : await this.links.find(alias);
      if (link && !isStopped(link)) {
        if (link.protected) {
          this.open.set(true);
          this.resolving.set(false);
          await this.router.navigate(['/preview', alias], { replaceUrl: true });
          return;
        }
        if (link.longUrl) {
          this.links.registerClick(alias);
          window.location.replace(link.longUrl);
          return;
        }
      }
      this.resolving.set(false);
      await this.router.navigate(['/404'], { replaceUrl: true });
    } catch {
      this.resolving.set(false);
    }
  }
}
